import React, { useState } from "react";
import { ChevronRight, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shadcn-components/ui/dialog";
import { useOrderLinesNew } from "./hooks/useOrderLinesNew";
import TradeOrderScreen from "@/components/trades-view/trade-order/TradeOrderScreen";
import {
  ChartTradeToolbar,
  chartToolbarBtn,
  chartToolbarBtnGhost,
  chartToolbarBtnOutline,
  chartToolbarDivider,
} from "@/components/chart/ChartTradeToolbar";
import { cn } from "@/lib/utils";

export type OrderType = "Buy Limit" | "Sell Limit" | "Buy Stop" | "Sell Stop";

interface SpecificTradePanelProps {
  onClose: () => void;
  chartInstanceRef: React.RefObject<any>;
  currentQuote: any;
  isVisible: boolean;
  onOrderLinePriceChange?: (orderType: OrderType, price: number) => void;
  selectedSymbol?: string;
  isDesktop?: boolean;
}

export const SpecificTradePanel = React.memo<SpecificTradePanelProps>(
  ({
    onClose,
    chartInstanceRef,
    currentQuote,
    isVisible,
    onOrderLinePriceChange,
    selectedSymbol,
    isDesktop = false,
  }) => {
    const [slEnabled, setSlEnabled] = useState<boolean>(false);
    const [tpEnabled, setTpEnabled] = useState<boolean>(false);
    const [showTradeDialog, setShowTradeDialog] = useState<boolean>(false);

    const {
      currentOrderType,
      setCurrentOrderType,
      slPrice,
      tpPrice,
      orderLinePrice,
      updateOrderLinePrice,
      updateSLLinePrice,
      updateTPLinePrice,
    } = useOrderLinesNew({
      chartInstanceRef,
      currentQuote,
      isVisible,
      slEnabled,
      tpEnabled,
      onPriceChange: onOrderLinePriceChange,
      onSLPriceChange: () => {},
      onTPPriceChange: () => {},
    });

    const orderTypes: OrderType[] = [
      "Buy Limit",
      "Sell Limit",
      "Buy Stop",
      "Sell Stop",
    ];

    const handleOrderTypeClick = () => {
      const currentIndex = orderTypes.indexOf(currentOrderType);
      const nextIndex = (currentIndex + 1) % orderTypes.length;
      setCurrentOrderType(orderTypes[nextIndex]);
    };

    const handleSL = () => {
      setSlEnabled(!slEnabled);
    };

    const handleTP = () => {
      setTpEnabled(!tpEnabled);
    };

    const handleMoreOptions = () => {
      setShowTradeDialog(true);
    };

    return (
      <>
        <ChartTradeToolbar visible={isVisible}>
          <button
            type="button"
            onClick={handleOrderTypeClick}
            className={cn(
              chartToolbarBtn,
              "max-w-[5.5rem] truncate bg-primary px-2 text-primary-foreground hover:bg-primary/90",
            )}
            title={`Order type: ${currentOrderType} (click to cycle)`}
            aria-label={`Order type: ${currentOrderType}, click to cycle`}
          >
            {currentOrderType}
          </button>

          <div className={chartToolbarDivider} aria-hidden />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleSL}
              className={cn(
                chartToolbarBtn,
                "px-2",
                slEnabled
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : chartToolbarBtnOutline,
              )}
              title={slEnabled ? "Remove stop loss" : "Add stop loss"}
              aria-label={slEnabled ? "Remove stop loss" : "Add stop loss"}
              aria-pressed={slEnabled}
            >
              SL
            </button>
            <button
              type="button"
              onClick={handleTP}
              className={cn(
                chartToolbarBtn,
                "px-2",
                tpEnabled
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : chartToolbarBtnOutline,
              )}
              title={tpEnabled ? "Remove take profit" : "Add take profit"}
              aria-label={tpEnabled ? "Remove take profit" : "Add take profit"}
              aria-pressed={tpEnabled}
            >
              TP
            </button>
          </div>

          <div className={chartToolbarDivider} aria-hidden />

          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={handleMoreOptions}
              className={chartToolbarBtnGhost}
              title="More options"
              aria-label="More options"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className={chartToolbarBtnGhost}
              title="Dismiss"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </ChartTradeToolbar>

        <Dialog open={showTradeDialog} onOpenChange={setShowTradeDialog}>
          <DialogContent
            className={`${
              isDesktop ? "max-w-md max-h-[70vh]" : "max-w-lg max-h-[80vh]"
            } p-4`}
          >
            <DialogHeader className="pb-2">
              <DialogTitle className="text-lg">Trade Order</DialogTitle>
            </DialogHeader>
            <div
              className={`${
                isDesktop ? "max-h-[55vh]" : "max-h-[60vh]"
              } overflow-y-auto`}
            >
              <TradeOrderScreen
                symbol={selectedSymbol}
                type="new"
                initialOrderType={currentOrderType}
                initialPrice={orderLinePrice > 0 ? orderLinePrice : undefined}
                initialStopLoss={slEnabled && slPrice > 0 ? slPrice : undefined}
                initialTakeProfit={
                  tpEnabled && tpPrice > 0 ? tpPrice : undefined
                }
                onPriceChange={updateOrderLinePrice}
                onStopLossChange={updateSLLinePrice}
                onTakeProfitChange={updateTPLinePrice}
                isDesktop={isDesktop}
                onBack={() => setShowTradeDialog(false)}
              />
            </div>
          </DialogContent>
        </Dialog>
      </>
    );
  },
);

SpecificTradePanel.displayName = "SpecificTradePanel";
